import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query'
import type React from 'react'
import { type PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react'
import { createIntegrationSourceGateway } from 'v2/shared/services/integration-source-gateway'
import {
  contactViewConfigQueryKeys,
  externalAppsQueryKeys,
  featureFlagsQueryKey,
  flowQueryKeys,
} from 'v2/shared/utils/query-key-management'
import type { IntegrationSource } from 'v2/types/api/settings'
import { useAuth } from './use-auth'
import { useFeatureFlags } from './use-feature-flags'
import {
  IntegrationRolloutContext,
  resolveIntegrationRollout,
} from './use-integration-rollout-context'

/**
 * Whether the feature-flag fetch failed outright — as opposed to "still loading".
 *
 * Read by subscribing to the flags query's own cache entry rather than by having
 * FeatureFlagsProvider publish it: this is a rollout concern, and the provider is
 * shared by consumers that neither need nor should carry it.
 *
 * `skipToken` subscribes WITHOUT ever fetching, so this observes the entry the real
 * hook owns and never issues a second request. The flags query sets `retry: false`,
 * so its error state is terminal and reached on the first failure.
 *
 * Deliberately NOT folded into the flags object as `isInitialData`: that means
 * "flags not fetched yet" (unknown), and use-livekit-enabled surfaces it as its own
 * isLoading so use-publish-flow can omit `livekit_ready` while unknown rather than
 * publish `false`. Rewriting it here would assert "LiveKit is off" for every org on
 * a transient flag outage.
 */
export const useFeatureFlagsFetchFailed = () => {
  const { details } = useAuth()
  const { isError } = useQuery({
    queryKey: featureFlagsQueryKey(details?.email, details?.organization_name),
    queryFn: skipToken,
  })
  return isError
}

const integrationCachePrefixes = [
  externalAppsQueryKeys().externalOrgAppsBase,
  externalAppsQueryKeys().externalAppsBase,
  externalAppsQueryKeys().externalAppActionsBase,
  externalAppsQueryKeys().summaryBase,
  externalAppsQueryKeys().entityDetailsBase,
  externalAppsQueryKeys().salesforceAccountBase,
  externalAppsQueryKeys().customFieldsBase,
  externalAppsQueryKeys().accessRecordsBase,
  contactViewConfigQueryKeys().contactViewConfigBase,
  contactViewConfigQueryKeys().contactSummaryBase,
  flowQueryKeys().appVariablesBase,
  flowQueryKeys().mcpToolsBase,
]

export const IntegrationRolloutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient()
  const flags = useFeatureFlags()
  const flagsFetchFailed = useFeatureFlagsFetchFailed()
  const { isResolved, mode, useAciIntegrations } = resolveIntegrationRollout(
    flags,
    flagsFetchFailed,
  )
  const previousMode = useRef<IntegrationSource>()
  const gateway = useMemo(
    () => createIntegrationSourceGateway({ aciEnabled: useAciIntegrations }),
    [useAciIntegrations],
  )

  useEffect(() => {
    if (!isResolved) return
    if (previousMode.current && previousMode.current !== mode) {
      void Promise.all(
        integrationCachePrefixes.map((queryKey) => queryClient.cancelQueries({ queryKey })),
      ).then(() => {
        for (const queryKey of integrationCachePrefixes) {
          queryClient.invalidateQueries({ queryKey })
        }
      })
    }
    previousMode.current = mode
  }, [isResolved, mode, queryClient])

  const value = useMemo(
    () => ({ gateway, isResolved, mode, useAciIntegrations }),
    [gateway, isResolved, mode, useAciIntegrations],
  )

  return (
    <IntegrationRolloutContext.Provider value={value}>
      {children}
    </IntegrationRolloutContext.Provider>
  )
}

export const useIntegrationRollout = () => useContext(IntegrationRolloutContext)
